import React, { useState, useEffect } from 'react';
import Button from '../ui/Button';
import FormInput from '../ui/FormInput';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sendOtp, verifyOtp } from '../../services/api/implementation/impl';
import Cookies from 'js-cookie';
import socket from '../../../../socket';
import mixpanel from 'mixpanel-browser';
import { ChevronDown, Mail } from 'lucide-react';
import HousinGramLogo from '../../../shared/assets/svg/Logo.svg';
import LoginFormFooter from './LoginFormFooter';
import { handlePostLoginStorage, saveAuthSessionCookies } from '../../utils/authStorage';

const LoginPhoneForm = () => {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { register, handleSubmit } = useForm();
  const phoneField = register('phone');

  const { mutate: handleSendOtp } = useMutation({
    mutationFn: async (payload) => await sendOtp(payload),
    onSuccess: async () => {
      toast.success('OTP sent successfully');
      setOtp('');
      setOtpSent(true);
      setIsProcessing(false);
    },
    onError: (err) => {
      console.error('Error sending OTP', err);
      toast.error('Error sending OTP');
      setIsProcessing(false);
    }
  });

  const { mutate: handleVerifyOtp } = useMutation({
    mutationFn: async (payload) => await verifyOtp(payload),

    onSuccess: async (res) => {
      console.log({ res });
      const userRole = res?.data?.data?.user?.role?.name;
      const userId = res?.data?.data?.user?.id;
      const userVertical = res?.data?.data?.user?.vertical;
      const userDesignationLevel = res?.data?.data?.user?.level;

      handlePostLoginStorage(userId);
      saveAuthSessionCookies(res.data.data);
      queryClient.clear();

      toast.success('User successfully logged in');

      mixpanel.identify(userId);
      mixpanel.people.set({
        $name: res?.data?.data?.user?.name,
        $phone: res?.data?.data?.user?.phone_number,
        $email: res?.data?.data?.user?.email,
        $vertical: res?.data?.data?.user?.vertical,
        $role: res?.data?.data?.user?.role?.name,
        $designation: res?.data?.data?.user?.designation?.name
      });

      mixpanel.track('User Logged In', {
        'Login Method': 'Phone'
      });

      window.dispatchEvent(
        new CustomEvent('userLoggedIn', {
          detail: {
            userId: userId,
            userRole: userRole,
            timestamp: new Date().toISOString()
          }
        })
      );

      if (userRole === 'builder') {
        if (userVertical === 'Sales') {
          navigate('/builder/sales');
        } else if (userVertical === 'Operations') {
          if (userDesignationLevel === 'Low') {
            toast.error('You are not authorized to access this page');
            Cookies.remove('token');
            Cookies.remove('userRoleId');
            Cookies.remove('userId');
            Cookies.remove('userDesignation');
            Cookies.remove('userRole');
            Cookies.remove('userVertical');
            Cookies.remove('userDesignationLevel');
            Cookies.remove('domino_enabled');
          } else if (userDesignationLevel === 'Medium' || userDesignationLevel === 'High') {
            navigate('/builder/dashboard');
          }
        } else if (userVertical === 'Accounts') {
          navigate('/builder/reports');
        } else if (userVertical === 'Legal') {
          navigate('/builder/legal');
        } else if (userVertical === 'Inventory') {
          if (userDesignationLevel === 'Low') {
            toast.error('You are not authorized to access this page');
            Cookies.remove('token');
            Cookies.remove('userRoleId');
            Cookies.remove('userId');
            Cookies.remove('userDesignation');
            Cookies.remove('userRole');
            Cookies.remove('userVertical');
            Cookies.remove('userDesignationLevel');
            Cookies.remove('domino_enabled');
          } else if (userDesignationLevel === 'Medium' || userDesignationLevel === 'High') {
            navigate('/builder/dashboard');
          }
        } else {
          navigate('/builder/dashboard');
        }
      } else if (userRole === 'admin') {
        navigate('/super-admin/home');
      }

      socket.connect();
      setIsProcessing(false);
    },
    onError: (err) => {
      console.error('Error verifying OTP', err);
      toast.error('Incorrect OTP');
      setIsProcessing(false);
    }
  });

  const onSubmitPhone = (data) => {
    if (!data.phone || data.phone.trim().length === 0 || data.phone.length !== 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    const payload = { phone: `+91${data.phone}` };
    handleSendOtp(payload);
  };

  const onSubmitOtp = (data) => {
    if (!data.otp || !data.otp.trim()) {
      toast.error('Please enter OTP');
      return;
    }

    const payload = { phone: `+91${phone}`, otp: data.otp.trim(), resend: false };
    handleVerifyOtp(payload);
  };

  const handlePhoneKeyDown = (e) => {
    if (e.key === 'Enter' && !isProcessing) {
      e.preventDefault();
      setIsProcessing(true);
      const phoneInput = document.querySelector('input[name="phone"]');
      const phoneValue = phoneInput?.value?.trim() || '';
      const payload = { phone: `+91${phoneValue}` };
      handleSendOtp(payload);
    }
  };

  const handleOtpKeyDown = (e) => {
    if (e.key === 'Enter' && !isProcessing) {
      e.preventDefault();
      setIsProcessing(true);
      const otpInput = document.querySelector('input[name="otp"]');
      const otpValue = otpInput?.value?.trim() || '';

      if (!otpValue) {
        toast.error('Please enter OTP');
        setIsProcessing(false);
        return;
      }

      const payload = { phone: `+91${phone}`, otp: otpValue, resend: false };
      handleVerifyOtp(payload);
    }
  };

  useEffect(() => {
    const setupEventListeners = () => {
      const phoneInput = document.querySelector('input[name="phone"]');
      const otpInput = document.querySelector('input[name="otp"]');

      if (phoneInput) {
        phoneInput.removeEventListener('keydown', handlePhoneKeyDown);
        phoneInput.addEventListener('keydown', handlePhoneKeyDown);
      }

      if (otpInput) {
        otpInput.removeEventListener('keydown', handleOtpKeyDown);
        otpInput.addEventListener('keydown', handleOtpKeyDown);
      }
    };

    const timeoutId = setTimeout(setupEventListeners, 100);

    return () => {
      clearTimeout(timeoutId);
      const phoneInput = document.querySelector('input[name="phone"]');
      const otpInput = document.querySelector('input[name="otp"]');

      if (phoneInput) {
        phoneInput.removeEventListener('keydown', handlePhoneKeyDown);
      }
      if (otpInput) {
        otpInput.removeEventListener('keydown', handleOtpKeyDown);
      }
    };
  }, [otpSent]);

  return (
    <form
      className="mx-auto flex min-h-full w-full max-w-md flex-col"
      onSubmit={handleSubmit(otpSent ? onSubmitOtp : onSubmitPhone)}>
      <div className="flex flex-col items-center gap-1 pt-2">
        <img src={HousinGramLogo} alt="HousinGram" className="h-11 w-auto" />
        <p className="text-center text-xs text-gray-500">
          Complete Real Estate Business Management Platform
        </p>
      </div>

      <div className="flex flex-1 flex-col justify-center gap-6 py-8">
        <div className="flex flex-col gap-2 text-center">
          <h1 className="text-2xl font-semibold text-stone-800">
            {otpSent ? 'Verify your number' : 'Welcome back to HousinGram!'}
          </h1>
          <p className="text-sm text-gray-500">
            {otpSent
              ? `Enter the OTP sent to +91 ${phone}`
              : 'Sign in to manage your projects, teams, sales and operations.'}
          </p>
        </div>

        {!otpSent ? (
          <>
            <div className="flex flex-col gap-2">
              <div className="flex w-fit items-center gap-1">
                <span className="text-sm text-gray-600">Phone Number</span>
                <span className="text-red-500">*</span>
              </div>
              <div className="flex overflow-hidden rounded-lg border border-gray-300 bg-white focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
                <div className="flex items-center gap-1.5 border-r border-gray-300 bg-gray-50 px-3 py-2.5">
                  <span className="text-base leading-none" role="img" aria-label="India">
                    🇮🇳
                  </span>
                  <span className="text-sm font-medium text-gray-700">+91</span>
                  <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                </div>
                <input
                  {...phoneField}
                  name="phone"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="Enter your mobile number"
                  className="w-full px-3 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none"
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    e.target.value = value;
                    phoneField.onChange(e);
                    setPhone(value);
                  }}
                />
              </div>
            </div>

            <Button type="submit" displayType="full-w-primary" width={100}>
              Send OTP
            </Button>

            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-sm text-gray-400">or</span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>

            <button
              type="button"
              onClick={() => navigate('/auth/login')}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-customBlue transition-colors hover:bg-gray-50">
              <Mail className="h-4 w-4" />
              Login via Email
            </button>
          </>
        ) : (
          <>
            <FormInput
              width={100}
              register={register}
              register_key="otp"
              label="OTP"
              type="text"
              isRequired
              onType={setOtp}
            />
            <Button type="submit" displayType="full-w-primary" width={100}>
              Verify OTP
            </Button>
            <button
              type="button"
              className="text-sm font-medium text-primary hover:underline"
              onClick={() => handleSendOtp({ phone: `+91${phone}` })}>
              Resend OTP
            </button>
            <button
              type="button"
              className="text-sm text-gray-500 hover:text-gray-700"
              onClick={() => {
                setOtpSent(false);
                setOtp('');
              }}>
              Change phone number
            </button>
          </>
        )}
      </div>

      <LoginFormFooter />
    </form>
  );
};

export default LoginPhoneForm;
